import { useRef, useState } from 'react'

import { useScheduledEffect } from 'slab/hooks/useScheduledEffect'

export function useScreenReaderAnnouncements ({
  container,
  selectedBadgeIndex,
}: {
  container: React.RefObject<HTMLDivElement>;
  selectedBadgeIndex: number | null;
}) {
  const [badgeValueChangeAnnouncement, setBadgeValueChangeAnnouncement] =
    useState<string>('')
  const [selectedBadgeAnnouncement, setSelectedBadgeAnnouncement] =
    useState<string>('')

  const scheduleBadgeValueChangeAnnouncement = useScheduledEffect()

  const latestSelectedBadgeIndex = useRef(selectedBadgeIndex)
  latestSelectedBadgeIndex.current = selectedBadgeIndex

  function getBadgeTextAtIndex (index: number) {
    const badges = container.current?.querySelectorAll('[data-badge]')

    return badges?.[index]?.textContent || ''
  }

  function announceAddedValue (value: string | string[]) {
    scheduleBadgeValueChangeAnnouncement('addedValue', () => {
      if (Array.isArray(value)) {
        setBadgeValueChangeAnnouncement(`Added values: ${value.join(', ')}`)
      } else {
        setBadgeValueChangeAnnouncement(`Added value: ${value}`)
      }

      const badgeIndex = latestSelectedBadgeIndex.current

      if (badgeIndex !== null) {
        const text = getBadgeTextAtIndex(badgeIndex)

        setSelectedBadgeAnnouncement(
          `Selected value: ${text}. Press Backspace or Delete key to remove.`
        )
      } else {
        setSelectedBadgeAnnouncement('')
      }
    })
  }

  function announceRemovedValue (value: string) {
    scheduleBadgeValueChangeAnnouncement('removedValue', () => {
      setBadgeValueChangeAnnouncement(`Removed value: ${value}`)

      const badgeIndex = latestSelectedBadgeIndex.current

      if (badgeIndex !== null) {
        const text = getBadgeTextAtIndex(badgeIndex)

        setSelectedBadgeAnnouncement(
          `Selected value: ${text}. Press Backspace or Delete key to remove.`
        )
      } else {
        setSelectedBadgeAnnouncement('')
      }
    })
  }

  function announceSelectionChange () {
    scheduleBadgeValueChangeAnnouncement('selectionChange', () => {
      const badgeIndex = latestSelectedBadgeIndex.current

      setBadgeValueChangeAnnouncement('')

      if (badgeIndex !== null) {
        const text = getBadgeTextAtIndex(badgeIndex)

        setSelectedBadgeAnnouncement(
          `Selected value: ${text}. Press Backspace or Delete key to remove.`
        )
      } else {
        setSelectedBadgeAnnouncement('')
      }
    })
  }

  function clearAnnouncements () {
    setBadgeValueChangeAnnouncement('')
    setSelectedBadgeAnnouncement('')
  }

  const announcement = [badgeValueChangeAnnouncement, selectedBadgeAnnouncement]
    .filter(Boolean)
    .join('. ')

  return {
    announcement,
    announceAddedValue,
    announceRemovedValue,
    announceSelectionChange,
    clearAnnouncements,
  }
}
